import moment from "moment";
import { CityEnum } from "../../../../city";
import { createFileRoute } from "@tanstack/react-router";
import { mockMessages } from "../../../../mocks/mockMessages";
import { isValidEmail } from "../../../../utils/emailValidator";
import { isValidPhoneNumber } from "../../../../utils/phoneValidator";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { formatPhoneNumber } from "../../../../utils/formatPhoneNumber";
import {
  Box,
  Paper,
  Button,
  TextField,
  Container,
  Typography,
  Autocomplete
} from "@mui/material";

export const Route = createFileRoute("/_app/agents/$agentId/")({
  component: RouteComponent
});

function RouteComponent() {
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [cities, setCities] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [emailAddressError, setEmailAddressError] = useState("");
  const [cityError, setCityError] = useState("");

  const handleSubmit = () => {
    let isValid = true;
    setCityError("");
    setLastNameError("");
    setFirstNameError("");
    setPhoneNumberError("");
    setEmailAddressError("");

    if (!firstName.trim()) {
      setFirstNameError("First name is required.");
      isValid = false;
    }

    if (!lastName.trim()) {
      setLastNameError("Last name is required.");
      isValid = false;
    }

    let isPhoneValid = false;
    try {
      isPhoneValid =
        isValidPhoneNumber(phoneNumber, "US") ||
        isValidPhoneNumber(phoneNumber, "CA");
    } catch (error) {}
    if (!isPhoneValid) {
      setPhoneNumberError("Invalid phone number.");
      isValid = false;
    }

    if (!emailAddress.trim()) {
      setEmailAddressError("Email address is required.");
      isValid = false;
    } else if (!isValidEmail(emailAddress)) {
      setEmailAddressError("Enter a valid email address.");
      isValid = false;
    }

    if (cities.length === 0) {
      setCityError("At least one city must be selected.");
      isValid = false;
    }

    if (isValid) {
      console.log("Validated Data:", {
        firstName,
        middleName,
        lastName,
        phoneNumber,
        emailAddress,
        cities
      });
    }
  };

  const handleSubmitMessage = () => {
    let isValid = true;
    setMessageError("");

    if (!message.trim()) {
      setMessageError("Message cannot be empty.");
      isValid = false;
    }

    if (isValid) {
      console.log("Validated Data:", {
        message
      });
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formattedNumber = formatPhoneNumber(e.target.value);
    setPhoneNumber(formattedNumber);
  };

  return (
    <Container
      sx={{
        mt: 5,
        maxWidth: {
          xs: "97%"
        }
      }}
      disableGutters={true}
    >
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {firstName ? firstName : "Agent"} Details
      </Typography>
      <Paper elevation={4} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Box
          component="form"
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "space-between",
              "& > *": {
                flex: "1 1 calc(50% - 8px)"
              },
              "@media (max-width: 600px)": {
                flexDirection: "column",
                "& > *": {
                  flex: "1 1 100%"
                }
              }
            }}
          >
            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              variant="outlined"
              required
              error={!!firstNameError}
              helperText={firstNameError}
            />
            <TextField
              fullWidth
              label="Middle Name"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              variant="outlined"
              required
              error={!!lastNameError}
              helperText={lastNameError}
            />
            <TextField
              fullWidth
              label="Phone Number"
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneChange}
              variant="outlined"
              required
              error={!!phoneNumberError}
              helperText={phoneNumberError}
            />
            <TextField
              fullWidth
              label="Email Address"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              variant="outlined"
              required
              error={!!emailAddressError}
              helperText={emailAddressError}
            />
            <Autocomplete
              multiple
              options={Object.values(CityEnum)}
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cities"
                  variant="outlined"
                  required
                  error={!!cityError}
                  helperText={cityError}
                />
              )}
              value={cities}
              onChange={(_, value) => setCities(value)}
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            sx={{
              alignSelf: "flex-end",
              textTransform: "none",
              fontWeight: "bold"
            }}
            onClick={handleSubmit}
          >
            Update
          </Button>
        </Box>
      </Paper>
      <Paper elevation={4} sx={{ borderRadius: 2, mb: 3 }}>
        <Typography
          sx={{ px: 3, pt: 3 }}
          variant="h6"
          fontWeight="bold"
          gutterBottom
        >
          Messages
        </Typography>
        <Box
          sx={{
            maxHeight: "300px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            px: 3
          }}
        >
          {mockMessages.map((message) => (
            <Box
              key={message.id}
              sx={{
                alignSelf: message.type === "post" ? "flex-end" : "flex-start",
                p: 2,
                borderRadius: 2,
                color: message.type === "post" ? "" : "#FECD07",
                maxWidth: "70%",
                border: "1px solid #ccc"
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "12px",
                  textAlign: message.type === "post" ? "right" : "left",

                  mb: 1
                }}
              >
                {moment(message.date).fromNow()}
              </Typography>
              <Typography variant="body2">{message.content}</Typography>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>
      </Paper>

      <Paper
        elevation={4}
        sx={{
          p: 3,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Send a Message
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          variant="outlined"
          required
          error={!!messageError}
          helperText={messageError}
        />
        <Button
          variant="contained"
          color="primary"
          sx={{
            mt: 2,
            textTransform: "none",
            fontWeight: "bold",
            alignSelf: "flex-end"
          }}
          onClick={handleSubmitMessage}
        >
          Send
        </Button>
      </Paper>
    </Container>
  );
}
